// Schema-driven definitions for admin entities. Keys = collection name.
// `fields`: form fields. type: text|number|textarea|image|csv (comma-separated string -> array)
// `listFields`: columns shown in the admin table.

export const ENTITY_SCHEMAS = {
  offers: {
    label: 'Offers',
    listFields: ['id', 'title', 'code', 'type', 'validity'],
    fields: [
      { name: 'id', label: 'ID', type: 'text', placeholder: 'OFF007 (auto if blank)' },
      { name: 'title', label: 'Title', type: 'text', required: true },
      { name: 'description', label: 'Description', type: 'textarea' },
      { name: 'code', label: 'Promo Code', type: 'text', required: true },
      { name: 'validity', label: 'Validity', type: 'text', placeholder: '31 Dec 2025' },
      { name: 'type', label: 'Service Type', type: 'text', placeholder: 'flights / hotels / buses' },
      { name: 'image', label: 'Image URL', type: 'image' },
    ],
  },
  destinations: {
    label: 'Destinations',
    listFields: ['id', 'city', 'country', 'description'],
    fields: [
      { name: 'id', label: 'ID', type: 'text' },
      { name: 'city', label: 'City', type: 'text', required: true },
      { name: 'country', label: 'Country', type: 'text', required: true },
      { name: 'description', label: 'Description', type: 'text' },
      { name: 'image', label: 'Image URL', type: 'image' },
    ],
  },
  flights: {
    label: 'Flights',
    listFields: ['id', 'airline', 'flightNumber', 'from', 'to', 'price'],
    fields: [
      { name: 'id', label: 'ID', type: 'text' },
      { name: 'airline', label: 'Airline', type: 'text', required: true },
      { name: 'flightNumber', label: 'Flight Number', type: 'text', required: true },
      { name: 'from', label: 'From City', type: 'text', required: true },
      { name: 'fromCode', label: 'From Code', type: 'text', placeholder: 'DEL' },
      { name: 'to', label: 'To City', type: 'text', required: true },
      { name: 'toCode', label: 'To Code', type: 'text', placeholder: 'BOM' },
      { name: 'departure', label: 'Departure Time', type: 'text', placeholder: '06:00' },
      { name: 'arrival', label: 'Arrival Time', type: 'text', placeholder: '08:15' },
      { name: 'duration', label: 'Duration', type: 'text', placeholder: '2h 15m' },
      { name: 'price', label: 'Price (₹)', type: 'number', required: true },
      { name: 'seats', label: 'Available Seats', type: 'number' },
      { name: 'class', label: 'Class', type: 'text', placeholder: 'Economy' },
      { name: 'stops', label: 'Stops', type: 'text', placeholder: 'Non-stop' },
    ],
  },
  hotels: {
    label: 'Hotels',
    listFields: ['id', 'name', 'location', 'area', 'rating', 'price'],
    fields: [
      { name: 'id', label: 'ID', type: 'text' },
      { name: 'name', label: 'Name', type: 'text', required: true },
      { name: 'location', label: 'City', type: 'text', required: true },
      { name: 'area', label: 'Area', type: 'text' },
      { name: 'rating', label: 'Rating', type: 'number', step: 0.1 },
      { name: 'reviews', label: 'Reviews Count', type: 'number' },
      { name: 'price', label: 'Price per Night (₹)', type: 'number', required: true },
      { name: 'roomType', label: 'Room Type', type: 'text' },
      { name: 'image', label: 'Image URL', type: 'image' },
      { name: 'amenities', label: 'Amenities (comma-separated)', type: 'csv' },
    ],
  },
  trains: {
    label: 'Trains',
    listFields: ['id', 'trainName', 'trainNumber', 'from', 'to', 'price'],
    fields: [
      { name: 'id', label: 'ID', type: 'text' },
      { name: 'trainName', label: 'Train Name', type: 'text', required: true },
      { name: 'trainNumber', label: 'Train Number', type: 'text', required: true },
      { name: 'from', label: 'From', type: 'text', required: true },
      { name: 'to', label: 'To', type: 'text', required: true },
      { name: 'departure', label: 'Departure', type: 'text' },
      { name: 'arrival', label: 'Arrival', type: 'text' },
      { name: 'duration', label: 'Duration', type: 'text' },
      { name: 'price', label: 'Price (₹)', type: 'number', required: true },
      { name: 'seats', label: 'Seats', type: 'number' },
      { name: 'class', label: 'Class', type: 'text', placeholder: '3A / 2A / SL' },
      { name: 'type', label: 'Type', type: 'text', placeholder: 'Superfast' },
    ],
  },
  buses: {
    label: 'Buses',
    listFields: ['id', 'operator', 'busType', 'from', 'to', 'price'],
    fields: [
      { name: 'id', label: 'ID', type: 'text' },
      { name: 'operator', label: 'Operator', type: 'text', required: true },
      { name: 'busType', label: 'Bus Type', type: 'text' },
      { name: 'from', label: 'From', type: 'text', required: true },
      { name: 'to', label: 'To', type: 'text', required: true },
      { name: 'departure', label: 'Departure', type: 'text' },
      { name: 'arrival', label: 'Arrival', type: 'text' },
      { name: 'duration', label: 'Duration', type: 'text' },
      { name: 'price', label: 'Price (₹)', type: 'number', required: true },
      { name: 'seats', label: 'Seats', type: 'number' },
      { name: 'rating', label: 'Rating', type: 'number', step: 0.1 },
      { name: 'amenities', label: 'Amenities (comma-separated)', type: 'csv' },
    ],
  },
  cabs: {
    label: 'Cabs',
    listFields: ['id', 'model', 'type', 'capacity', 'price', 'rating'],
    fields: [
      { name: 'id', label: 'ID', type: 'text' },
      { name: 'type', label: 'Type', type: 'text', placeholder: 'Sedan / SUV / Hatchback' },
      { name: 'model', label: 'Model', type: 'text', required: true },
      { name: 'capacity', label: 'Capacity', type: 'number' },
      { name: 'price', label: 'Price', type: 'number', required: true },
      { name: 'priceUnit', label: 'Price Unit', type: 'text', placeholder: 'per km' },
      { name: 'rating', label: 'Rating', type: 'number', step: 0.1 },
      { name: 'features', label: 'Features (comma-separated)', type: 'csv' },
      { name: 'image', label: 'Image URL', type: 'image' },
    ],
  },
  villas: {
    label: 'Villas & Homestays',
    listFields: ['id', 'name', 'location', 'type', 'price', 'rating'],
    fields: [
      { name: 'id', label: 'ID', type: 'text' },
      { name: 'name', label: 'Name', type: 'text', required: true },
      { name: 'location', label: 'Location', type: 'text', required: true },
      { name: 'area', label: 'Area', type: 'text' },
      { name: 'type', label: 'Type', type: 'text', placeholder: 'Villa / Homestay' },
      { name: 'rating', label: 'Rating', type: 'number', step: 0.1 },
      { name: 'reviews', label: 'Reviews Count', type: 'number' },
      { name: 'price', label: 'Price per Night (₹)', type: 'number', required: true },
      { name: 'bedrooms', label: 'Bedrooms', type: 'number' },
      { name: 'maxGuests', label: 'Max Guests', type: 'number' },
      { name: 'image', label: 'Image URL', type: 'image' },
      { name: 'amenities', label: 'Amenities (comma-separated)', type: 'csv' },
    ],
  },
  packages: {
    label: 'Holiday Packages',
    listFields: ['id', 'name', 'destination', 'duration', 'price', 'rating'],
    fields: [
      { name: 'id', label: 'ID', type: 'text' },
      { name: 'name', label: 'Package Name', type: 'text', required: true },
      { name: 'destination', label: 'Destination', type: 'text', required: true },
      { name: 'duration', label: 'Duration', type: 'text', placeholder: '5 Nights / 6 Days' },
      { name: 'price', label: 'Price (₹)', type: 'number', required: true },
      { name: 'rating', label: 'Rating', type: 'number', step: 0.1 },
      { name: 'image', label: 'Image URL', type: 'image' },
      { name: 'inclusions', label: 'Inclusions (comma-separated)', type: 'csv' },
      { name: 'highlights', label: 'Highlights (comma-separated)', type: 'csv' },
    ],
  },
  tours: {
    label: 'Tours',
    listFields: ['id', 'name', 'location', 'duration', 'price', 'rating'],
    fields: [
      { name: 'id', label: 'ID', type: 'text' },
      { name: 'name', label: 'Tour Name', type: 'text', required: true },
      { name: 'location', label: 'Location', type: 'text', required: true },
      { name: 'duration', label: 'Duration', type: 'text' },
      { name: 'price', label: 'Price (₹)', type: 'number', required: true },
      { name: 'rating', label: 'Rating', type: 'number', step: 0.1 },
      { name: 'reviews', label: 'Reviews', type: 'number' },
      { name: 'image', label: 'Image URL', type: 'image' },
      { name: 'inclusions', label: 'Inclusions (comma-separated)', type: 'csv' },
    ],
  },
  visas: {
    label: 'Visa Services',
    listFields: ['id', 'country', 'type', 'price', 'processingTime'],
    fields: [
      { name: 'id', label: 'ID', type: 'text' },
      { name: 'country', label: 'Country', type: 'text', required: true },
      { name: 'type', label: 'Visa Type', type: 'text', required: true },
      { name: 'price', label: 'Price (₹)', type: 'number', required: true },
      { name: 'processingTime', label: 'Processing Time', type: 'text' },
      { name: 'image', label: 'Image URL', type: 'image' },
      { name: 'documents', label: 'Required Documents (comma-separated)', type: 'csv' },
    ],
  },
  cruises: {
    label: 'Cruises',
    listFields: ['id', 'name', 'duration', 'route', 'price', 'rating'],
    fields: [
      { name: 'id', label: 'ID', type: 'text' },
      { name: 'name', label: 'Cruise Name', type: 'text', required: true },
      { name: 'duration', label: 'Duration', type: 'text' },
      { name: 'route', label: 'Route', type: 'text' },
      { name: 'cabinType', label: 'Cabin Type', type: 'text' },
      { name: 'price', label: 'Price (₹)', type: 'number', required: true },
      { name: 'rating', label: 'Rating', type: 'number', step: 0.1 },
      { name: 'image', label: 'Image URL', type: 'image' },
      { name: 'amenities', label: 'Amenities (comma-separated)', type: 'csv' },
    ],
  },
  forex: {
    label: 'Forex Services',
    listFields: ['id', 'type', 'currency', 'provider', 'rate'],
    fields: [
      { name: 'id', label: 'ID', type: 'text' },
      { name: 'type', label: 'Service Type', type: 'text', placeholder: 'Forex Card / Currency Exchange' },
      { name: 'currency', label: 'Currency', type: 'text' },
      { name: 'provider', label: 'Provider', type: 'text' },
      { name: 'price', label: 'Price (₹)', type: 'number' },
      { name: 'rate', label: 'Exchange Rate', type: 'number', step: 0.01 },
      { name: 'service', label: 'Service Description', type: 'text' },
      { name: 'image', label: 'Image URL', type: 'image' },
      { name: 'features', label: 'Features (comma-separated)', type: 'csv' },
    ],
  },
  insurance: {
    label: 'Insurance Plans',
    listFields: ['id', 'name', 'coverage', 'duration', 'price', 'provider'],
    fields: [
      { name: 'id', label: 'ID', type: 'text' },
      { name: 'name', label: 'Plan Name', type: 'text', required: true },
      { name: 'coverage', label: 'Coverage Amount', type: 'text', placeholder: '₹10,00,000' },
      { name: 'duration', label: 'Duration', type: 'text', placeholder: 'Per Trip / Annual' },
      { name: 'price', label: 'Price (₹)', type: 'number', required: true },
      { name: 'provider', label: 'Provider', type: 'text' },
      { name: 'features', label: 'Features (comma-separated)', type: 'csv' },
    ],
  },
};

export const ENTITY_KEYS = Object.keys(ENTITY_SCHEMAS);
